import { useRegisterSW } from 'virtual:pwa-register/react';

// Minimum time between update checks (30 seconds)
const UPDATE_THROTTLE_MS = 30 * 1000;

// Custom event name for SPA navigation
const SPA_NAVIGATION_EVENT = 'spa-navigation';

/**
 * Patch history methods to dispatch custom events on SPA navigation.
 * This is done once globally to intercept all navigation in React Router
 * and other libraries that use the History API.
 */
function patchHistoryMethods() {
  // Only patch once
  if ((window as unknown as { __historyPatched?: boolean }).__historyPatched) {
    return;
  }
  (window as unknown as { __historyPatched?: boolean }).__historyPatched = true;

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    const result = originalPushState(...args);
    window.dispatchEvent(new CustomEvent(SPA_NAVIGATION_EVENT));
    return result;
  };

  history.replaceState = function (
    ...args: Parameters<typeof history.replaceState>
  ) {
    const result = originalReplaceState(...args);
    window.dispatchEvent(new CustomEvent(SPA_NAVIGATION_EVENT));
    return result;
  };
}

/**
 * Hook to manage PWA service worker updates
 * Returns state and methods to handle app updates
 *
 * Checks for updates:
 * - Immediately on page load
 * - When app becomes visible (tab switch or PWA foreground)
 * - When app resumes from sleep/background
 * - When network reconnects (user was offline)
 * - When page navigates (SPA navigation via React Router)
 * - Every hour for long sessions
 *
 * All checks are throttled to prevent redundant requests when
 * multiple events fire simultaneously (e.g., focus + visibilitychange)
 */
export function usePWAUpdate() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (r) {
        let lastUpdateCheck = 0;

        // Throttled update check to prevent multiple simultaneous checks
        const checkForUpdate = () => {
          const now = Date.now();
          if (now - lastUpdateCheck > UPDATE_THROTTLE_MS) {
            lastUpdateCheck = now;
            r.update();
          }
        };

        // Patch history methods to detect SPA navigation
        patchHistoryMethods();

        // Check immediately on page load
        checkForUpdate();

        // Check when app becomes visible (works for both tabs and standalone PWA)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            checkForUpdate();
          }
        });

        // Check when app resumes from sleep/background (standalone PWA)
        window.addEventListener('resume', () => {
          checkForUpdate();
        });

        // Check when user focuses the window (additional trigger for PWA)
        window.addEventListener('focus', () => {
          checkForUpdate();
        });

        // Check when network reconnects (user was offline)
        window.addEventListener('online', () => {
          checkForUpdate();
        });

        // Check on browser back/forward navigation
        window.addEventListener('popstate', () => {
          checkForUpdate();
        });

        // Check on SPA navigation (React Router pushState/replaceState)
        window.addEventListener(SPA_NAVIGATION_EVENT, () => {
          checkForUpdate();
        });

        // Also check periodically (every hour) for long sessions
        setInterval(checkForUpdate, 60 * 60 * 1000);
      }
    },
  });

  const update = () => {
    updateServiceWorker(true);
  };

  const dismiss = () => {
    setNeedRefresh(false);
  };

  return {
    needRefresh,
    update,
    dismiss,
  };
}
